import { formatStatusLabel } from './helpers';

const normalize = (value) => String(value ?? '').replace(/\s+/g, ' ').trim();

/**
 * Rephrase file-pipeline "Run now" API responses for end users.
 */
export function humanizePipelineRunResponse(data) {
  const status = normalize(data?.status).toLowerCase();
  const requeued = Number(data?.files_requeued || 0);
  const raw = normalize(data?.message).toLowerCase();

  if (status === 'already_running') {
    if (raw.includes('lock')) {
      return 'Another worker is already processing files for this project. Please wait a few minutes and try again.';
    }
    return 'Files are already being processed for this project. Please wait for the current run to finish.';
  }

  if (requeued > 0) {
    return `${requeued} file(s) queued for processing. The pipeline is running in the background.`;
  }

  if (raw.includes('selected-file')) {
    return 'Processing started for the selected file(s) in the background.';
  }

  return 'File processing started in the background.';
}

/**
 * Rephrase project configuration save API responses.
 */
export function humanizeConfigSaveResponse(data, context = {}) {
  if (!data || typeof data !== 'object') {
    return 'Changes saved successfully.';
  }

  const pipeline = data.pipeline_reindex;
  const raw = normalize(data.message).toLowerCase();
  const changeType = normalize(context.changeType).toLowerCase();
  const newBucket = normalize(context.newBucket);
  const isKnowledgeSourceChange = (
    changeType === 's3_change'
    || raw.includes('re-index from s3')
    || raw.includes('new bucket')
  );

  if (pipeline?.pipeline_triggered) {
    const count = Number(pipeline.files_requeued || 0);
    const graphNote = raw.includes('graphrag') || raw.includes('neo4j')
      ? ' Knowledge graph entries will be rebuilt when you push files to Neo4j.'
      : '';
    if (isKnowledgeSourceChange) {
      if (count === 0) {
        return newBucket
          ? (
            `Knowledge Source updated to "${newBucket}". The search index was reset. `
            + 'Upload or sync files under upload/, then use Run now on the RAG Pipeline tab.'
            + graphNote
          )
          : (
            'Knowledge Source updated. The search index was reset. '
            + 'Upload or sync your files, then use Run now on the RAG Pipeline tab.'
            + graphNote
          );
      }
      return newBucket
        ? (
          `Knowledge Source updated to "${newBucket}". ${count} file(s) `
          + `are being reprocessed in the background.${graphNote}`
        )
        : (
          `Knowledge Source updated. ${count} file(s) `
          + `are being reprocessed in the background.${graphNote}`
        );
    }
    if (count === 0) {
      return (
        'Changes saved. The search index was reset. '
        + 'Upload or sync your files, then use Run now on the RAG Pipeline tab to process them.'
        + graphNote
      );
    }
    return (
      `Changes saved. The search index was reset and ${count} file(s) `
      + `are being reprocessed in the background.${graphNote}`
    );
  }

  if (raw.includes('vectors were copied') || raw.includes('copied to the new collection')) {
    const match = raw.match(/(\d+)\s+entit/);
    const countText = match ? ` (${match[1]} items copied)` : '';
    return `Changes saved. Existing search data was copied to the new index${countText}. Chat is ready without re-processing files.`;
  }

  if (data.status_reset || raw.includes('statuses reset to pending')) {
    return 'Changes saved. Files need to be processed again — use Run now on the RAG Pipeline tab.';
  }

  if (
    data.deployment_status_preserved
    || raw.includes('deployment status preserved')
    || raw.includes('query-time parameters only')
  ) {
    return 'Changes saved. Your knowledge base is unchanged.';
  }

  if (raw.includes('configuration updated')) {
    return 'Changes saved successfully.';
  }

  return 'Changes saved successfully.';
}

export function humanizeCollectionDeleteSuccess(collection, responseData) {
  const projectName = collection?.project_name || 'The project';
  if (responseData && responseData.success === false) {
    return `${projectName} was partially deleted. Some cleanup steps may still need attention.`;
  }
  return `${projectName} and its search index were permanently deleted.`;
}

export function humanizeCollectionDeleteWarning(collection, responseData) {
  const projectName = collection?.project_name || 'The project';
  const raw = normalize(responseData?.message).toLowerCase();
  if (raw.includes('failed to delete project')) {
    return `The search index was removed, but ${projectName} could not be fully deleted. Please contact support if this persists.`;
  }
  return `${projectName} was deleted with warnings. Review the project list and try again if needed.`;
}

export function humanizeCollectionRestoreSuccess(collection) {
  const projectName = collection?.project_name || 'The project';
  return `${projectName} has been restored and is visible on the dashboard again.`;
}

export function humanizeCollectionRestoreFailure(collection) {
  const projectName = collection?.project_name || 'The project';
  return `Could not restore ${projectName}. Please try again or contact support.`;
}

export function formatReuploadSuccess(fileName) {
  const label = normalize(fileName) || 'File';
  return `"${label}" was uploaded again successfully. Processing will continue automatically.`;
}

export function formatFileUploadSuccess(fileName) {
  const label = normalize(fileName) || 'File';
  return `"${label}" was uploaded successfully.`;
}

export function formatRebuildVectorsSuccess(pushedCount, entitiesAfter) {
  const pushed = Number(pushedCount || 0);
  const after = entitiesAfter == null ? null : Number(entitiesAfter);
  if (after != null && Number.isFinite(after)) {
    return `Search index rebuilt from saved data (${pushed} chunk(s) indexed; about ${after} searchable items in total).`;
  }
  return `Search index rebuilt from saved data (${pushed} chunk(s) indexed).`;
}

export function formatKnowledgeSourceVerified(bucket, { pendingSave = false, forCreate = false } = {}) {
  const name = normalize(bucket);
  if (pendingSave && name) {
    return `Knowledge Source access confirmed for "${name}". Click Save to apply this bucket to the project.`;
  }
  if (forCreate && name) {
    return `Knowledge Source access confirmed for "${name}". You can continue creating the project.`;
  }
  if (name) {
    return `Knowledge Source access confirmed for "${name}".`;
  }
  return 'Knowledge Source configuration verified successfully.';
}

export function formatS3FileDeletedFromBucket(fileName) {
  const label = normalize(fileName) || 'The file';
  return `"${label}" was removed from the Knowledge Source bucket.`;
}

export function formatS3FileRemovedPendingCleanup(fileName) {
  const label = normalize(fileName) || 'The file';
  return `"${label}" was removed from the Knowledge Source. Indexed data is being cleaned up.`;
}

export function formatProjectDeleted({ emptyBucket = false, bucketName } = {}) {
  if (emptyBucket) {
    const name = normalize(bucketName);
    return name
      ? `Project deleted and all files were removed from the "${name}" bucket.`
      : 'Project deleted and the Knowledge Source bucket was emptied.';
  }
  return 'Project deleted successfully.';
}

export function formatNeo4jPushSuccess(count) {
  const n = Number(count || 0);
  return n === 1
    ? '1 file was added to the knowledge graph (Neo4j).'
    : `${n} files were added to the knowledge graph (Neo4j).`;
}

export function formatNeo4jPushPartialSuccess(ingested, failed) {
  const ok = Number(ingested || 0);
  const bad = Number(failed || 0);
  return `${formatNeo4jPushSuccess(ok).replace(/\.$/, '')}, but ${bad} file(s) could not be added.`;
}

export function formatNeo4jRemoveSuccess(count) {
  const n = Number(count || 0);
  return n === 1
    ? '1 file was removed from the knowledge graph (Neo4j).'
    : `${n} files were removed from the knowledge graph (Neo4j).`;
}

export function formatNeo4jRemovePartialSuccess(removed, failed) {
  const ok = Number(removed || 0);
  const bad = Number(failed || 0);
  return `${formatNeo4jRemoveSuccess(ok).replace(/\.$/, '')}, but ${bad} file(s) could not be removed.`;
}

export function formatVectorRetentionRemoved(count) {
  const n = Number(count || 0);
  return n === 1
    ? 'Expired vectors were removed for 1 document.'
    : `Expired vectors were removed for ${n} documents.`;
}

export function formatStrategySelected(strategy) {
  const label = formatStatusLabel(strategy, { fallback: 'Selected' });
  return `${label} chunking strategy selected. You can continue to the next step.`;
}

export function formatAuditClearSuccess(deletedCount, { scope = 'Guardrail audit history' } = {}) {
  const n = Number(deletedCount || 0);
  return n === 1
    ? `${scope}: 1 record removed.`
    : `${scope}: ${n} records removed.`;
}

export function formatVectorDbHealthySkip(count) {
  const n = Number(count || 0);
  return `The search index is healthy and already contains ${n} vector(s). A rebuild is not needed.`;
}
